/* eslint-disable max-len */
import { BankFilled } from '@ant-design/icons';
import cn from 'classnames';
import { FieldInputProps, FieldMetaProps } from 'formik';
import React, { HTMLProps, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import Button from '~/components/Button/Button';
import { Text } from '~/components/Typo/Typo';
import CustomTextField from '~/components/fields/CustomTextField/CustomTextField';
import { FontWeight, TextVariants } from '~/theme/constants';
import { formatOptCode } from '~/utils/handle-input-events';
import './BankOtpForm.scss';

export interface OtpFormProps extends HTMLProps<HTMLFormElement> {
  getFieldProps: (name: string) => FieldInputProps<any>;
  getFieldMeta: (name: string) => FieldMetaProps<any>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  showMessageOtp: boolean;
  loading?: boolean;
  hideTimer?: boolean;
  setHideTimer: (value: boolean) => void;
  askNewOtp: () => Promise<any>;
  toast: any;
  setShowButtonPay: (value: boolean) => void;
  disableButtonValue: boolean;
}

const BankOtpForm: React.FC<OtpFormProps> = ({
  loading,
  getFieldProps,
  getFieldMeta,
  handleChange,
  showMessageOtp,
  hideTimer,
  setHideTimer,
  askNewOtp,
  toast,
  setShowButtonPay,
  disableButtonValue,
}) => {
  const { t } = useTranslation();

  const expiryTimestamp = new Date();
  expiryTimestamp.setMinutes(expiryTimestamp.getMinutes() + 3);

  const { seconds, minutes, hours, days, restart } = useTimer({
    onExpire: () => showbtnGetOtp(),
    expiryTimestamp,
  });

  const showbtnGetOtp = useCallback(() => {
    console.warn('onExpire called');
    setHideTimer(true);
  }, [hideTimer]);

  useEffect(() => {
    setShowButtonPay(true);
  }, [disableButtonValue]);

  const restartTimer = useCallback(() => {
    setShowButtonPay(true);
    // Call Api to get new OTP
    const a = askNewOtp();
    a.then(() => {
      toast.success(t('otpForm:enterOtpCodeReceived'));
      setHideTimer(false);
      const time = new Date();
      time.setMinutes(time.getMinutes() + 3);
      restart(time);
    }).catch(() => {
      toast.success(t('bank:errorInit'));
      setHideTimer(true);
    });
  }, [restart, setHideTimer]);

  return (
    <div className="otp-payment-form">
      <div className={cn('caption-wrapper')}>
        <Text className="caption" size="s" weight={FontWeight.MEDIUM} variant={TextVariants.PRIMARY}>
          {t('otpForm:infoOtpCode')}
        </Text>
      </div>
      <div className="form-wrapper">
        <CustomTextField
          name="otp"
          type="text"
          label={t('otpForm:otpCode') + ' (GW-XXXXXX)'}
          placeholder="GW-XXXXXX"
          meta={getFieldMeta('otp')}
          field={getFieldProps('otp')}
          onChange={handleChange}
          onInput={formatOptCode}
          inputMode="numeric"
          suffixIcon={<BankFilled className="suffix-icon" />}
        />
        <div className="submit-button-wrapper">
          <Button title={t('otpForm:send')} size="block" rounded type="submit" disabled={loading} loading={loading} />
        </div>
        <div className="line">
          {hideTimer ? (
            <>
              <div className="centered-text">
                <Button
                  title={t('otpForm:resendOtp')}
                  size="s"
                  rounded
                  type="button"
                  disabled={loading}
                  loading={loading}
                  onClick={restartTimer}
                />
              </div>
            </>
          ) : (
            <>
              <div className="centered-text">
                <p>
                  <span>{days}</span>:<span>{hours}</span>:<span>{minutes}</span>:<span>{seconds}</span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="centered-text">{showMessageOtp && <div className="text-danger">{t('bank:invalidOtp')}</div>}</div>
    </div>
  );
};

export default BankOtpForm;
